import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { BannerSeverity, TenantBanner } from '../lib/types';
import { SEVERITY_TONE, DismissButton } from '../lib/severity';

const CTA: Record<BannerSeverity, string> = {
  info: 'text-brand-dark hover:text-brand',
  warning: 'text-[#8C6700] hover:text-[#B8860B]',
  error: 'text-state-error hover:opacity-80',
};

type RenderedBanner = {
  id: string;
  severity: BannerSeverity;
  title?: string;
  text: string;
  link_url?: string | null;
  link_label?: string | null;
  dismissible: boolean;
};

function fromTenantBanner(b: TenantBanner): RenderedBanner {
  return {
    id: b.id,
    severity: b.severity,
    text: b.text,
    link_url: b.link_url,
    link_label: b.link_label,
    dismissible: b.dismissible,
  };
}

function CtaButton({ banner }: { banner: RenderedBanner }) {
  if (!banner.link_url || !banner.link_label) return null;
  const cls = `inline-flex items-center gap-1 shrink-0 text-sm font-medium ${CTA[banner.severity]} hover:underline`;
  // The backend guarantees URLs start with '/' (internal) or 'https://' (external).
  if (banner.link_url.startsWith('/')) {
    return <Link to={banner.link_url} className={cls}>{banner.link_label}</Link>;
  }
  return (
    <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className={cls}>
      {banner.link_label}
      <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

/**
 * Renders every banner — both control-plane-pushed banners (trial countdown,
 * past-due, etc.) and the synthesised read-only/archived status banner
 * derived from tenant.status. Status banner is prepended and non-dismissible.
 */
export default function BannerStack() {
  const { banners, dismissBanner, status, isReadOnly } = useWorkspace();

  const rendered: RenderedBanner[] = [];
  if (isReadOnly) {
    rendered.push(
      status === 'archived'
        ? {
            id: '__status:archived',
            severity: 'error',
            title: 'Archived',
            text: 'this workspace has been archived. No changes can be made.',
            dismissible: false,
          }
        : {
            id: '__status:past_due',
            severity: 'warning',
            title: 'Read-only',
            text: 'your subscription requires attention. Contact your administrator to restore write access.',
            dismissible: false,
          },
    );
  }
  for (const b of banners) rendered.push(fromTenantBanner(b));

  if (rendered.length === 0) return null;

  return (
    <div className="space-y-2 shrink-0">
      {rendered.map(b => {
        const tone = SEVERITY_TONE[b.severity];
        const Icon = tone.Icon;
        return (
          <div
            key={b.id}
            role={b.severity === 'error' ? 'alert' : 'status'}
            className={`flex items-center gap-3 rounded-xl border border-l-4 px-3 py-3 ${tone.card}`}
          >
            <span className={`shrink-0 ${tone.icon}`}>
              <Icon className="w-5 h-5" />
            </span>
            <div className="flex-1 text-sm leading-snug text-graphite">
              {b.title && <b className="font-semibold">{b.title}</b>}
              {b.title && ' — '}
              {b.text}
            </div>
            <CtaButton banner={b} />
            {b.dismissible && (
              <DismissButton onClick={() => dismissBanner(b.id)} title="Dismiss" />
            )}
          </div>
        );
      })}
    </div>
  );
}
